import { useCallback, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { unpackAndMergeDynamicSchemaFields } from '../../../core/utils';
import { GET_LIST_OF_SERVICES_UNSAVED_DATA } from '../queries';
import { IChapter } from '../pages/Assistance';

interface UseChapterPrefetchingProps {
    recordId: string;
    chapterMap: Map<string, IChapter> | null;
    setChapterMap: (updater: (prevMap: Map<string, IChapter>) => Map<string, IChapter>) => void;
    loadedChapters: Set<string>;
    markChapterAsLoaded: (chapterNumber: string) => void;
}

export const useChapterPrefetching = ({
    recordId,
    chapterMap,
    setChapterMap,
    loadedChapters,
    markChapterAsLoaded,
}: UseChapterPrefetchingProps) => {
    const prefetchingRef = useRef(false);
    const loadedChaptersRef = useRef<Set<string>>(new Set());
    const chapterMapRef = useRef<Map<string, IChapter>>(new Map());
    // Track whether we've already triggered a prefetch for this chapterMap identity.
    // This prevents re-triggering prefetch on every chapterMap state update.
    const hasPrefetchedRef = useRef(false);

    const [getPositions] = useLazyQuery(GET_LIST_OF_SERVICES_UNSAVED_DATA, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });

    // Keep refs in sync with state
    useEffect(() => {
        loadedChaptersRef.current = loadedChapters;
        if (loadedChapters.size === 0) {
            hasPrefetchedRef.current = false;
        }
    }, [loadedChapters]);

    useEffect(() => {
        chapterMapRef.current = chapterMap || new Map();
    }, [chapterMap]);

    // Prefetch all unloaded chapters simultaneously
    const prefetchAllChapters = useCallback(async () => {
        if (prefetchingRef.current || !chapterMapRef.current || chapterMapRef.current.size === 0) return;

        // Find all unloaded chapters
        const chaptersToLoad: string[] = [];
        chapterMapRef.current.forEach((chapter, chapterNumber) => {
            if (chapter.positions.length === 0 && !chapter.isLoading && !loadedChaptersRef.current.has(chapterNumber)) {
                chaptersToLoad.push(chapterNumber);
            }
        });

        if (chaptersToLoad.length === 0) {
            return;
        }

        prefetchingRef.current = true;

        // Mark all chapters as loading
        setChapterMap((prevMap) => {
            const updatedMap = new Map(prevMap);
            chaptersToLoad.forEach((chapterNumber) => {
                const chapter = updatedMap.get(chapterNumber);
                if (chapter) {
                    updatedMap.set(chapterNumber, { ...chapter, isLoading: true });
                }
            });
            return updatedMap;
        });

        try {
            // Load all chapters simultaneously
            const prefetchPromises = chaptersToLoad.map(async (chapterNumber) => {
                try {
                    const result = await getPositions({
                        variables: {
                            id: recordId,
                            chapter: chapterNumber,
                        },
                    });
                    return { chapterNumber, data: result.data, success: true };
                } catch (error) {
                    console.error(`Failed to prefetch chapter ${chapterNumber}:`, error);
                    return { chapterNumber, data: null, success: false };
                }
            });

            const results = await Promise.all(prefetchPromises);

            // Process each successful result individually
            const successfulChapters: string[] = [];
            results.forEach(({ chapterNumber, data, success }) => {
                if (!success || !data) return;

                const loadedPositions = unpackAndMergeDynamicSchemaFields(
                    data.listOfServicesProcessingRecord.listOfServicesUnsaved?.positions || []
                );

                loadedPositions.forEach((position, index) => {
                    position.indexInChapter = index;
                });

                if (loadedPositions.length > 0) {
                    // Update the chapter map with the new positions
                    setChapterMap((prevMap) => {
                        const updatedMap = new Map(prevMap);
                        const chapter = updatedMap.get(chapterNumber);
                        if (chapter) {
                            const updatedChapter = {
                                ...chapter,
                                positions: loadedPositions,
                                isLoading: false,
                            };
                            updatedMap.set(chapterNumber, updatedChapter);
                        }
                        return updatedMap;
                    });

                    successfulChapters.push(chapterNumber);
                }
            });

            // Mark successful chapters as loaded
            successfulChapters.forEach((chapterNumber) => markChapterAsLoaded(chapterNumber));
        } catch (error) {
            console.error('Failed to prefetch chapters:', error);
        } finally {
            // Mark all chapters as not loading
            setChapterMap((prevMap) => {
                const updatedMap = new Map(prevMap);
                chaptersToLoad.forEach((chapterNumber) => {
                    const chapter = updatedMap.get(chapterNumber);
                    if (chapter) {
                        updatedMap.set(chapterNumber, { ...chapter, isLoading: false });
                    }
                });
                return updatedMap;
            });

            prefetchingRef.current = false;
        }
    }, [getPositions, recordId, setChapterMap, markChapterAsLoaded]);

    // Auto-prefetch when chapterMap first becomes available (transitions from null to a Map).
    // We use a ref flag to avoid re-triggering on subsequent chapterMap state updates.
    // The flag is reset when chapterMap goes back to null (e.g. on refetch).
    useEffect(() => {
        if (!chapterMap) {
            // Reset so we re-prefetch after the next initialization
            hasPrefetchedRef.current = false;
            return;
        }

        if (hasPrefetchedRef.current || prefetchingRef.current) {
            return;
        }

        const timer = setTimeout(() => {
            hasPrefetchedRef.current = true;
            prefetchAllChapters();
        }, 100);

        return () => clearTimeout(timer);
    }, [chapterMap, prefetchAllChapters]);

    return {
        prefetchAllChapters,
    };
};
